import React, { useEffect, useState } from "react";
import { BookOpenCheck } from "lucide-react";
import { Modal, Spinner } from "../lib/ui";
import { rp } from "./finance";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export const ENTRY_TYPE_META = {
  auto: { label: "🟢 Otomatis", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  manual: { label: "🔵 Manual", cls: "border-blue-200 bg-blue-50 text-blue-700" },
  adjustment: { label: "🟡 Penyesuaian", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  reversal: { label: "🔴 Pembalikan", cls: "border-red-200 bg-red-50 text-red-700" },
};

export const SOURCE_LABEL = {
  sales: "Penjualan", pos_sales: "Mini POS", purchases: "Pembelian", finance: "Keuangan",
  payroll: "Payroll", production: "Produksi", kasbon: "Kasbon", waste: "Waste",
  incident: "Insiden", opex: "Biaya Operasional", petty_cash: "Petty Cash",
  petty_topup: "Top Up Petty Cash", inventory: "Pergerakan Stok", opening: "Saldo Awal",
  manual: "Manual", reversal: "Pembalikan", closing: "Tutup Buku",
};

export function JournalTypeBadge({ journal, className = "" }) {
  const meta = ENTRY_TYPE_META[journal?.entry_type || "auto"] || ENTRY_TYPE_META.auto;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls} ${className}`} data-testid="journal-type-badge">
      {meta.label}
    </span>
  );
}

export function JournalLinesTable({ journal }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line" data-testid={`journal-lines-${journal.id}`}>
      <table className="w-full text-xs">
        <thead className="bg-stone-50 text-left text-[10px] uppercase text-ink-mute">
          <tr><th className="px-2.5 py-1.5">Akun</th><th className="px-2.5 py-1.5 text-right">Debit</th><th className="px-2.5 py-1.5 text-right">Kredit</th></tr>
        </thead>
        <tbody className="divide-y divide-line">
          {(journal.lines || []).map((l, i) => (
            <tr key={i}>
              <td className={`px-2.5 py-1.5 text-ink ${l.credit ? "pl-6" : ""}`}>{l.account_code} · {l.account_name}</td>
              <td className="px-2.5 py-1.5 text-right tabular-nums">{l.debit ? rp(l.debit) : ""}</td>
              <td className="px-2.5 py-1.5 text-right tabular-nums">{l.credit ? rp(l.credit) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Priority D/4 — Accounting Journal Preview embedded on operational transaction pages.
 *  module: source module or reference type (pos_sales, purchases, purchase_order,
 *  stock_transfer, opname, order, waste, petty_cash, opex, production, ...) */
export function JournalPreview({ module, docId, className = "", title = "Pratinjau Jurnal Akuntansi" }) {
  const { hasPermission } = useAuth();
  const canView = hasPermission("view_accounting");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!canView || !docId) return;
    let alive = true;
    api.get("/accounting/journals/by-source", { params: { module, doc_id: docId } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData({ journals: [], count: 0, total: 0 }); });
    return () => { alive = false; };
  }, [module, docId, canView]);
  if (!canView) return null;
  return (
    <div className={`rounded-xl border border-line bg-app p-3.5 ${className}`} data-testid={`journal-preview-${module}`}>
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-bold text-ink-soft"><BookOpenCheck size={14} className="text-primary" /> {title}</p>
        {data && data.count > 0 && <span className="text-[11px] font-semibold text-ink-mute">{data.count} jurnal · {rp(data.total)}</span>}
      </div>
      {!data ? (
        <div className="flex h-14 items-center justify-center"><Spinner className="h-5 w-5" /></div>
      ) : data.count === 0 ? (
        <p className="text-xs text-ink-mute" data-testid="journal-preview-empty">Belum ada jurnal untuk transaksi ini.</p>
      ) : (
        <div className="space-y-2.5">
          {data.journals.map((j) => (
            <div key={j.id} data-testid={`journal-preview-item-${j.id}`}>
              <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="font-semibold text-ink">{j.description}</span>
                <JournalTypeBadge journal={j} />
                {j.source_doc_number && <span className="font-mono text-[10px] text-ink-mute">{j.source_doc_number}</span>}
              </div>
              <JournalLinesTable journal={j} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact trigger for table rows — opens the preview in a modal. */
export function JournalPreviewButton({ module, docId, label = "Jurnal" }) {
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  if (!hasPermission("view_accounting")) return null;
  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
        title="Pratinjau Jurnal Akuntansi" data-testid={`journal-preview-btn-${docId}`}>
        <BookOpenCheck size={13} /> {label}
      </button>
      {open && (
        <Modal open onClose={() => setOpen(false)} title="Pratinjau Jurnal Akuntansi" size="lg">
          <JournalPreview module={module} docId={docId} title="Jurnal dari transaksi ini" />
        </Modal>
      )}
    </>
  );
}
